/**
 * Iter 67x — Vehicle feature catalog with colour-coded icon chips.
 *
 * Each feature is matched against `car.features` (free-text array stored on the
 * car doc) via case-insensitive substring → so legacy listings that wrote
 * "Air-Conditioning" still resolve to the A/C entry. Unknown strings render as
 * neutral chips so vendors can keep adding bespoke amenities.
 *
 * Tailwind requires class names to be statically reachable at build time, so
 * the tone classes are spelled out below — NEVER construct them dynamically.
 */
import {
  Snowflake, Disc, ShieldAlert, Lock, Wind, Bluetooth, Navigation, Gauge,
  Radar, Camera, Usb, Sun, Flame, Baby, Lightbulb, Plug, Car as CarIcon,
  Star,
} from "lucide-react";

export const FEATURE_CATALOG = [
  { key: "ac",                label: "A/C",                  icon: Snowflake,   matches: ["a/c", "ac", "air con", "climate", "aircon"] },
  { key: "abs",               label: "ABS",                  icon: Disc,        matches: ["abs", "anti-lock"] },
  { key: "airbags",           label: "Air Bags",             icon: ShieldAlert, matches: ["airbag", "air bag", "air-bag"] },
  { key: "central_locking",   label: "Central Locking",      icon: Lock,        matches: ["central lock", "central-lock", "remote lock"] },
  { key: "electric_windows",  label: "Electric Windows",     icon: Wind,        matches: ["electric window", "power window"] },
  { key: "bluetooth",         label: "Bluetooth",            icon: Bluetooth,   matches: ["bluetooth", "wireless audio"] },
  { key: "gps",               label: "GPS Navigation",       icon: Navigation,  matches: ["gps", "navigation", "sat nav"] },
  { key: "cruise_control",    label: "Cruise Control",       icon: Gauge,       matches: ["cruise"] },
  { key: "parking_sensors",   label: "Parking Sensors",      icon: Radar,       matches: ["parking sensor", "park sensor", "pdc"] },
  { key: "rear_camera",       label: "Rear Camera",          icon: Camera,      matches: ["rear camera", "reversing camera", "back-up camera", "backup camera"] },
  { key: "usb",               label: "USB Charger",          icon: Usb,         matches: ["usb"] },
  { key: "sunroof",           label: "Sunroof",              icon: Sun,         matches: ["sunroof", "moonroof", "panoramic roof"] },
  { key: "heated_seats",      label: "Heated Seats",         icon: Flame,       matches: ["heated seat"] },
  { key: "child_seat",        label: "Child Seat Ready",     icon: Baby,        matches: ["child seat", "baby seat", "booster"] },
  { key: "auto_lights",       label: "Automatic Lights",     icon: Lightbulb,   matches: ["automatic light", "auto light", "auto headlight"] },
  { key: "isofix",            label: "ISOFIX",               icon: Plug,        matches: ["isofix"] },
];

// Static palette — Tailwind-safe (no dynamic class construction).
export const FEATURE_TONE = {
  ac:                { bg: "bg-sky-50",     fg: "text-sky-700",     ring: "ring-sky-200",     dot: "bg-sky-500" },
  abs:               { bg: "bg-emerald-50", fg: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" },
  airbags:           { bg: "bg-rose-50",    fg: "text-rose-700",    ring: "ring-rose-200",    dot: "bg-rose-500" },
  central_locking:   { bg: "bg-amber-50",   fg: "text-amber-700",   ring: "ring-amber-200",   dot: "bg-amber-500" },
  electric_windows:  { bg: "bg-violet-50",  fg: "text-violet-700",  ring: "ring-violet-200",  dot: "bg-violet-500" },
  bluetooth:         { bg: "bg-blue-50",    fg: "text-blue-700",    ring: "ring-blue-200",    dot: "bg-blue-500" },
  gps:               { bg: "bg-orange-50",  fg: "text-orange-700",  ring: "ring-orange-200",  dot: "bg-orange-500" },
  cruise_control:    { bg: "bg-teal-50",    fg: "text-teal-700",    ring: "ring-teal-200",    dot: "bg-teal-500" },
  parking_sensors:   { bg: "bg-pink-50",    fg: "text-pink-700",    ring: "ring-pink-200",    dot: "bg-pink-500" },
  rear_camera:       { bg: "bg-indigo-50",  fg: "text-indigo-700",  ring: "ring-indigo-200",  dot: "bg-indigo-500" },
  usb:               { bg: "bg-yellow-50",  fg: "text-yellow-700",  ring: "ring-yellow-200",  dot: "bg-yellow-500" },
  sunroof:           { bg: "bg-orange-50",  fg: "text-orange-700",  ring: "ring-orange-200",  dot: "bg-orange-500" },
  heated_seats:      { bg: "bg-red-50",     fg: "text-red-700",     ring: "ring-red-200",     dot: "bg-red-500" },
  child_seat:        { bg: "bg-fuchsia-50", fg: "text-fuchsia-700", ring: "ring-fuchsia-200", dot: "bg-fuchsia-500" },
  auto_lights:       { bg: "bg-lime-50",    fg: "text-lime-700",    ring: "ring-lime-200",    dot: "bg-lime-500" },
  isofix:            { bg: "bg-cyan-50",    fg: "text-cyan-700",    ring: "ring-cyan-200",    dot: "bg-cyan-500" },
  // Fallback for unknown freetext entries.
  _custom:           { bg: "bg-neutral-100",fg: "text-neutral-700", ring: "ring-neutral-300", dot: "bg-neutral-400" },
};

/** Resolve a free-text feature label into a catalog entry, or null. */
export function matchFeature(text) {
  const t = (text || "").toString().toLowerCase().trim();
  if (!t) return null;
  for (const f of FEATURE_CATALOG) {
    if (f.matches.some((m) => t.includes(m))) return f;
  }
  return null;
}

/** Render-ready chips for a `car.features` list. Custom strings get the
 *  neutral palette + the generic Star icon so vendors can keep listing
 *  anything they want. */
export function featureChipsFromList(features = []) {
  const out = [];
  const seen = new Set();
  for (const raw of features) {
    const match = matchFeature(raw);
    if (match) {
      if (seen.has(match.key)) continue;
      seen.add(match.key);
      out.push({ key: match.key, label: match.label, icon: match.icon, tone: FEATURE_TONE[match.key] });
    } else if (typeof raw === "string" && raw.trim()) {
      const slug = `_c_${raw.trim().toLowerCase().replace(/\s+/g, "_")}`;
      if (seen.has(slug)) continue;
      seen.add(slug);
      out.push({ key: slug, label: raw.trim(), icon: Star, tone: FEATURE_TONE._custom });
    }
  }
  return out;
}

export { CarIcon };
